/**
 * QueryHistorySearchPage
 *
 * Command palette drill-down page for searching query history across all
 * connection/database contexts with filters. Rendered inside the CommandPalette
 * when initialPage === 'query-history'.
 */

import { useMemo, useState, useCallback } from 'react';
import { Search, Calendar } from 'lucide-react';
import { CommandGroup } from '@/components/ui/command';
import { useQueryHistoryStore } from '../store/queryHistoryStore';
import { useConnectionStore } from '../../connections/store/connectionStore';
import { useCommandPaletteStore } from '../../command-palette/store/commandPaletteStore';
import { useWorkspaceStore } from '../../workspace';
import {
  searchQueryHistory,
  getUniqueConnectionIds,
  getUniqueDatabases,
  defaultSearchFilters,
  type QueryHistorySearchFilters,
  type DateRangeFilter,
  type SuccessFilter,
} from '../utils/queryHistorySearch';
import { FilterChip, HistoryResultItem } from './HistorySearchItems';
import type { QueryHistoryEntry } from '../types/queryHistoryTypes';
import { safeConnectionName, safeServerText } from '@/types/safeConnectionDisplay';

/** Max results to render in the list */
const MAX_RESULTS = 50;

const dateRangeLabels: Record<DateRangeFilter, string> = {
  'all': 'All time',
  'today': 'Today',
  'last-7-days': 'Last 7 days',
  'last-30-days': 'Last 30 days',
};

const successLabels: Record<SuccessFilter, string> = {
  'all': 'All status',
  'success': 'Success',
  'error': 'Error',
};

type QueryHistorySearchPageProps = {
  /** Search text from the command palette input */
  search?: string;
};

export function QueryHistorySearchPage({ search = '' }: QueryHistorySearchPageProps) {
  const historyByContext = useQueryHistoryStore((s) => s.historyByContext);
  const connections = useConnectionStore((s) => s.connections);
  const close = useCommandPaletteStore((s) => s.close);
  const activeTabId = useWorkspaceStore((s) => s.activeTabId);
  const updateTabContent = useWorkspaceStore((s) => s.updateTabContent);

  const [filters, setFilters] = useState<QueryHistorySearchFilters>({ ...defaultSearchFilters });

  // Build connection name lookup
  const connectionNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    for (const conn of connections) {
      map[conn.id] = safeConnectionName(conn);
    }
    return map;
  }, [connections]);

  // Available filter options
  const connectionIds = useMemo(() => getUniqueConnectionIds(historyByContext), [historyByContext]);
  const databases = useMemo(
    () => getUniqueDatabases(historyByContext, filters.connectionId),
    [historyByContext, filters.connectionId],
  );

  // Merge the command palette search text into filters
  const activeFilters = useMemo(
    () => ({ ...filters, searchText: search || filters.searchText }),
    [filters, search],
  );

  // Search results
  const allResults = useMemo(
    () => searchQueryHistory(historyByContext, activeFilters),
    [historyByContext, activeFilters],
  );

  const results = useMemo(() => allResults.slice(0, MAX_RESULTS), [allResults]);
  const totalCount = allResults.length;

  const handleSelect = useCallback((entry: QueryHistoryEntry) => {
    close();
    // Insert SQL into the active query editor tab
    if (activeTabId) {
      setTimeout(() => {
        updateTabContent(activeTabId, { query: entry.sql });
      }, 0);
    }
  }, [close, activeTabId, updateTabContent]);

  const toggleConnectionFilter = useCallback((connId: string) => {
    setFilters((f) => ({
      ...f,
      connectionId: f.connectionId === connId ? undefined : connId,
      database: f.connectionId === connId ? f.database : undefined,
    }));
  }, []);

  const toggleDatabaseFilter = useCallback((db: string) => {
    setFilters((f) => ({
      ...f,
      database: f.database === db ? undefined : db,
    }));
  }, []);

  const cycleDateRange = useCallback(() => {
    const order: DateRangeFilter[] = ['all', 'today', 'last-7-days', 'last-30-days'];
    setFilters((f) => {
      const idx = order.indexOf(f.dateRange);
      return { ...f, dateRange: order[(idx + 1) % order.length] };
    });
  }, []);

  const cycleSuccessFilter = useCallback(() => {
    const order: SuccessFilter[] = ['all', 'success', 'error'];
    setFilters((f) => {
      const idx = order.indexOf(f.successFilter);
      return { ...f, successFilter: order[(idx + 1) % order.length] };
    });
  }, []);

  return (
    <>
      {/* Filter bar */}
      <div
        className="flex flex-wrap gap-1.5 px-3 py-2 border-b border-border/50"
        onKeyDown={(e) => e.stopPropagation()}
      >
        {connectionIds.map((connId) => (
          <FilterChip
            key={connId}
            label={connectionNameMap[connId] || connId.slice(0, 8)}
            active={filters.connectionId === connId}
            onClick={() => toggleConnectionFilter(connId)}
          />
        ))}

        {filters.connectionId && databases.map((db) => (
          <FilterChip
            key={db}
            label={db}
            active={filters.database === db}
            onClick={() => toggleDatabaseFilter(db)}
          />
        ))}

        <FilterChip
          label={dateRangeLabels[filters.dateRange]}
          active={filters.dateRange !== 'all'}
          onClick={cycleDateRange}
        />

        <FilterChip
          label={successLabels[filters.successFilter]}
          active={filters.successFilter !== 'all'}
          onClick={cycleSuccessFilter}
        />
      </div>

      {/* Results */}
      <CommandGroup
        heading={
          totalCount > 0
            ? `Query History — ${totalCount} ${totalCount === 1 ? 'result' : 'results'}${totalCount > MAX_RESULTS ? ` (showing ${MAX_RESULTS})` : ''}`
            : 'Query History'
        }
      >
        {results.length === 0 ? (
          <div className="px-3 py-6 text-center text-xs text-muted-foreground" data-testid="history-search-empty">
            <Search className="h-5 w-5 mx-auto mb-2 opacity-40" />
            <p>No matching queries found</p>
            {/* PP-17: full-opacity `text-muted-foreground`, not `/60` — measured
                ~2.51:1 (light) / ~3.04:1 (dark) at `/60`, well under WCAG AA's
                4.5:1; full opacity measures ~5.65:1 / ~5.93:1. */}
            <p className="mt-1 text-muted-foreground">Execute some queries first, then search here</p>
          </div>
        ) : (
          results.map((entry) => (
            <HistoryResultItem
              key={entry.id}
              entry={entry}
              connectionName={connectionNameMap[entry.connectionId] || safeServerText(entry.connectionName) || entry.connectionId.slice(0, 8)}
              onSelect={() => handleSelect(entry)}
            />
          ))
        )}
      </CommandGroup>

      {/* Footer hint. PP-17: full-opacity `text-muted-foreground`, not
          `/60` — same measured contrast failure as the empty-state line
          above (~2.51:1 / ~3.04:1 vs ~5.65:1 / ~5.93:1 at full opacity). */}
      <div className="border-t border-border px-3 py-1.5 text-[10px] text-muted-foreground flex items-center gap-2">
        <span>Click to replace editor content</span>
        <span className="ml-auto">
          <Calendar className="h-2.5 w-2.5 inline mr-0.5" />
          Click chips to filter
        </span>
      </div>
    </>
  );
}
